import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import type { ToolRuntimeMissingRequirement } from '@shared/types/prebuilt-tools';
import type { ToolFieldDefinition } from '@shared/types/tool-manifest';

interface ToolLaunchDialogProps {
  open: boolean;
  toolTitle: string;
  contextLabel?: string | null;
  composerTitle: string;
  submitLabel: string;
  fields: ToolFieldDefinition[];
  values: Record<string, string>;
  missingRequirement: ToolRuntimeMissingRequirement | null;
  hasActiveRun: boolean;
  runtimeLoading: boolean;
  runtimeSubmitting: boolean;
  submitDisabled: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: () => void;
  onRefresh: () => void;
  onValueChange: (key: string, value: string) => void;
}

export const ToolLaunchDialog = ({
  open,
  toolTitle,
  contextLabel,
  composerTitle,
  submitLabel,
  fields,
  values,
  missingRequirement,
  hasActiveRun,
  runtimeLoading,
  runtimeSubmitting,
  submitDisabled,
  error,
  onClose,
  onSubmit,
  onRefresh,
  onValueChange,
}: ToolLaunchDialogProps) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-domino-ink/30 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tool-launch-dialog-title"
        className="max-h-[94vh] w-full max-w-2xl overflow-hidden rounded-t-[24px] border border-domino-cardBorder bg-domino-white shadow-2xl sm:max-h-[88vh] sm:rounded-[24px]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-domino-cardBorder px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <h2
              id="tool-launch-dialog-title"
              className="text-lg font-semibold tracking-tight text-domino-ink sm:text-xl"
            >
              {composerTitle}
            </h2>
            {contextLabel && (
              <p className="mt-1 text-sm text-domino-inkSoft">
                Pour {contextLabel} · les informations déjà connues sont reprises ci-dessous.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[calc(94vh-130px)] overflow-y-auto px-5 py-5 sm:max-h-[calc(88vh-130px)] sm:px-6">
          {hasActiveRun && (
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-domino-cardBorder pb-4">
              <p className="text-sm text-domino-inkSoft">
                Une action est déjà en cours dans {toolTitle}.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onRefresh}
                loading={runtimeLoading}
                className="gap-2"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </Button>
            </div>
          )}

          {missingRequirement?.isConnector ? (
            <div className="border-l-2 border-amber-400 pl-4">
              <p className="text-sm font-semibold text-domino-ink">
                {missingRequirement.title}
              </p>
              <p className="mt-2 text-sm leading-6 text-domino-inkSoft">
                {missingRequirement.description}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              {fields.map((field) => {
                const value = values[field.key] ?? '';

                return (
                  <div key={field.key}>
                    <label
                      htmlFor={`tool-dialog-field-${field.key}`}
                      className="text-sm font-medium text-domino-ink"
                    >
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={`tool-dialog-field-${field.key}`}
                        value={value}
                        onChange={(event) => onValueChange(field.key, event.target.value)}
                        rows={field.rows ?? 6}
                        placeholder={field.placeholder}
                        className="mt-2 rounded-[24px] border-domino-cardBorder bg-domino-white"
                      />
                    ) : field.type === 'select' ? (
                      <select
                        id={`tool-dialog-field-${field.key}`}
                        value={value || field.options?.[0]?.value || ''}
                        onChange={(event) => onValueChange(field.key, event.target.value)}
                        className="mt-2 flex h-11 w-full rounded-[18px] border border-domino-cardBorder bg-domino-white px-4 text-sm text-domino-ink shadow-sm outline-none transition focus:border-domino-prune focus:ring-2 focus:ring-domino-lilac"
                      >
                        {field.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        id={`tool-dialog-field-${field.key}`}
                        type={field.type}
                        value={value}
                        onChange={(event) => onValueChange(field.key, event.target.value)}
                        placeholder={field.placeholder}
                        className="mt-2 h-11 rounded-[18px] border-domino-cardBorder bg-domino-white"
                      />
                    )}
                    {field.description && (
                      <p className="mt-1.5 text-xs leading-5 text-domino-inkSoft">
                        {field.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-domino-cardBorder px-5 py-4 sm:px-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            {missingRequirement?.isConnector && (
              <Button
                type="button"
                variant="secondary"
                onClick={onRefresh}
                loading={runtimeLoading}
              >
                Actualiser
              </Button>
            )}
            <Button
              type="button"
              onClick={onSubmit}
              disabled={submitDisabled}
              loading={runtimeSubmitting}
            >
              {missingRequirement?.isConnector
                ? missingRequirement.ctaLabel
                : submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
